// Diálogo modal centrado: título, cuerpo libre y los dos botones de siempre
// (cancelar / confirmar). Para confirmaciones y para pedir un dato corto.
//
// Va por PORTAL al `body`: dentro de un `Drawer` el panel lleva `transform`, y un
// ancestro transformado se convierte en el bloque contenedor de todo `fixed`
// descendiente — el diálogo se pintaría encajado en el panel en vez de a
// pantalla completa. Responde a Escape sólo si es la capa de encima.
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { useCapaEscape } from '@/components/common/Drawer'
import { Loader } from '@/components/common/Loader'

interface Props {
  dark: boolean
  titulo: string
  children: ReactNode
  confirmarTexto: string
  cancelarTexto: string
  onConfirmar: () => void
  onCancelar: () => void
  /** Confirmar apagado (faltan datos). */
  confirmarDeshabilitado?: boolean
  /** Petición en vuelo: confirmar apagado y con rueda. */
  ocupado?: boolean
  /** Rechazo ya traducido, bajo el cuerpo. */
  error?: string | null
}

export function DialogoModal({
  dark, titulo, children, confirmarTexto, cancelarTexto, onConfirmar, onCancelar,
  confirmarDeshabilitado = false, ocupado = false, error,
}: Props) {
  useCapaEscape(true, () => { if (!ocupado) onCancelar() })
  const apagado = confirmarDeshabilitado || ocupado

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
        onClick={() => { if (!ocupado) onCancelar() }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className={`relative w-full max-w-md rounded-xl border p-5 shadow-2xl ${
          dark ? 'bg-slate-900 border-white/10 text-slate-100' : 'bg-white border-slate-200 text-slate-900'
        }`}
      >
        <h3 className="text-[14px] font-bold mb-2">{titulo}</h3>
        <div className={`text-[12px] leading-relaxed ${dark ? 'text-slate-300' : 'text-slate-600'}`}>
          {children}
        </div>
        {error && (
          <p role="alert" className={`mt-3 text-[12px] rounded-lg px-3 py-2 ${
            dark ? 'bg-rose-500/10 text-rose-200' : 'bg-rose-50 text-rose-700'
          }`}>
            {error}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancelar}
            disabled={ocupado}
            className={`text-[12px] font-semibold px-3 py-1.5 rounded-lg border cursor-pointer disabled:opacity-50 ${
              dark ? 'border-white/10 text-slate-300 hover:bg-white/8' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {cancelarTexto}
          </button>
          <button
            type="button"
            onClick={onConfirmar}
            disabled={apagado}
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-sky-600 text-white hover:bg-sky-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            {ocupado && <Loader size={4} />}
            {confirmarTexto}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
